"use client";

import { ProductAttributeKeys } from "@wttb/product-configurator";
import cn from "clsx";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { BiError } from "react-icons/bi";

import { ArtworkChecks, ProductAttributeInfo, ProductPage } from "@/clients/contentful";
import { LoyaltyTier } from "@/common/constants/loyalty";
import { useCartLimit } from "@/common/hooks/use-cart-limit";
import { config as appConfig } from "@/config";
import { useMe } from "@/features/account/hooks/use-me";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import Button from "../../../common/components/button";
import { productTestIds } from "../test-ids";
import { AddToCartButton, AddToCartButtonChildrenProps } from "./add-to-cart-button";
import { AmendOrderButton, AmendOrderButtonChildrenProps } from "./amend-order-button";
import { LoyaltyPriceBadge } from "./loyalty-price-badge";
import { ShoppingListModalForm } from "./modals/shopping-list-modal-form";
import { PriceInfo } from "./price-info";
import { ProductArtworkCheck } from "./product-artwork-check";
import { ProductAttributesRenderer } from "./product-attributes/product-attributes-renderer";
import { ProductOrderSummary } from "./product-order-summary";
import { ProductProductionUpgrade } from "./product-production-upgrade";
import { UpdateCartItemButton, UpdateCartItemButtonChildrenProps } from "./update-cart-item-button";

type ProductConfiguratorProps = {
  product: ProductPage;
  attributesInfo: Record<string, ProductAttributeInfo>;
  artworkChecks?: ArtworkChecks;
};

export const ProductConfigurator = ({
  product,
  attributesInfo,
  artworkChecks,
}: ProductConfiguratorProps) => {
  const t = useTranslations("common");
  const { data: user } = useSession();
  const { data: customer } = useMe(user);
  const cartLimitReached = useCartLimit();
  const [isShoppingListModalOpen, setIsShoppingListModalOpen] = useState(false);
  const { config, sku, isError, isConfigValid, updateOrderId, updateLineItemId, printProvider } =
    useStoreProduct();

  const error = useMemo(
    () =>
      !!config?.[ProductAttributeKeys.SequentialNumbering] &&
      !config?.[ProductAttributeKeys.SequentialNumberingValue] &&
      t("text-sequential-numbering-error"),
    [config],
  );

  if (!config) return null;

  return (
    <>
      <div className="mb-2 mt-6 flex flex-col items-center lg:mb-8 lg:mt-11">
        <span className="text-2xl font-semibold">{t("product-configurator-heading")}</span>
        <span className="text-base lg:text-2xl">{t("product-configurator-subheading")}</span>
      </div>
      <div className="border-grey-40 mx-auto mt-2 flex w-full flex-col items-center border-b bg-white pb-3 md:w-full lg:pb-6">
        <ProductAttributesRenderer attributesInfo={attributesInfo} />
      </div>

      <ProductProductionUpgrade />

      <ProductArtworkCheck artworkChecks={artworkChecks} />

      <ProductOrderSummary title={product.fields.title} />

      <div
        className={cn("mt-7 flex items-center relative -mx-4 p-4 lg:mx-0 lg:mt-10", {
          "justify-between":
            customer != null && customer?.custom?.fields.loyaltyTierKey !== LoyaltyTier.BLUE,
          "justify-center":
            customer == null || customer?.custom?.fields.loyaltyTierKey === LoyaltyTier.BLUE,
        })}
      >
        <PriceInfo
          className={
            customer == null || customer?.custom?.fields.loyaltyTierKey === LoyaltyTier.BLUE
              ? "justify-center items-center"
              : undefined
          }
          testId={productTestIds.productSummaryPrice}
        />

        <div className="lg:block">
          <LoyaltyPriceBadge />
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        {/* Add product to the cart */}
        {!updateOrderId && !updateLineItemId && (
          <AddToCartButton product={product}>
            {({ isLoading, handleAddToBasket }: AddToCartButtonChildrenProps) => (
              <Button
                onClick={handleAddToBasket}
                loading={isLoading}
                variant="success"
                className={cn("h-14 w-full !text-2xl !font-semibold uppercase", {
                  "!text-xl": cartLimitReached,
                })}
                disabled={isError || isLoading || !isConfigValid || cartLimitReached}
                data-testid={productTestIds.addToBasketBtn}
              >
                <span id="add-to-cart-btn-text">
                  {cartLimitReached
                    ? t.rich("text-basket-limit-reached", {
                        items: () => appConfig.NEXT_PUBLIC_CART_LIMIT,
                      })
                    : t("text-add-to-cart")}
                </span>
              </Button>
            )}
          </AddToCartButton>
        )}

        {/* Update line item in the cart */}
        {!updateOrderId && updateLineItemId && (
          <UpdateCartItemButton sku={sku} lineItemId={updateLineItemId}>
            {({ isLoading, handleUpdateCartItem }: UpdateCartItemButtonChildrenProps) => (
              <Button
                onClick={handleUpdateCartItem}
                loading={isLoading}
                variant="success"
                className={`h-14 w-full !text-2xl !font-semibold uppercase`}
                disabled={isError || isLoading || !isConfigValid}
              >
                <span id="add-to-cart-btn-text">{t("button-update-item")}</span>
              </Button>
            )}
          </UpdateCartItemButton>
        )}

        {/* Update line item in the order */}
        {updateOrderId && updateLineItemId && (
          <AmendOrderButton
            productSku={product.fields.sku!}
            orderId={updateOrderId}
            lineItemId={updateLineItemId}
          >
            {({ isLoading, handleAmendOrderLineItem }: AmendOrderButtonChildrenProps) => (
              <Button
                onClick={handleAmendOrderLineItem}
                loading={isLoading}
                variant="success"
                className={`h-14 w-full !text-2xl !font-semibold uppercase`}
                disabled={isError || isLoading || !isConfigValid}
              >
                <span id="add-to-cart-btn-text">{t("button-amend-order-item")}</span>
              </Button>
            )}
          </AmendOrderButton>
        )}
      </div>
      {user && !user.isAnonymous && (
        <div className="flex justify-center pt-6">
          <ShoppingListModalForm
            sku={product.fields.sku!}
            config={config}
            printProvider={printProvider}
            isOpen={isShoppingListModalOpen}
            handleOnClose={() => setIsShoppingListModalOpen(false)}
          />
          <Button
            onClick={() => setIsShoppingListModalOpen(true)}
            variant="secondary"
            data-testid={productTestIds.saveToShoppingListBtn}
            className={`h-14 w-full !text-2xl !font-semibold uppercase`}
          >
            {t("text-save-to-shopping-list")}
          </Button>
        </div>
      )}

      {error && (
        <div className="bg-danger-light text-danger mt-6 flex items-center justify-center p-3 text-[15px]">
          <BiError size={27} className="mr-2.5" />
          {error}
        </div>
      )}
    </>
  );
};
